import { Provider as TooltipProvider } from '@radix-ui/react-tooltip'
import HybridTooltip from '@trustoo/components/ui/tooltip/HybridTooltip'
import { twMerge } from 'tailwind-merge'

export interface PasswordStrengthCopy {
    /** What a zxcvbn score of 0, 1, 2, and 3 or higher means. */
    scores: [string, string, string, string]
    /** How to pick a strong password. */
    hint: string
}

export interface PasswordStrengthProps {
    /** The zxcvbn score of the password: 0 is too guessable, 4 very unguessable. */
    score: number
    copy: PasswordStrengthCopy
    className?: string
}

const BAR_COUNT = 4

/**
 * A meter of four bars that fill up with the strength of a password. Hovering or tapping it says
 * what the strength means.
 */
const PasswordStrength = ({ score, copy, className }: PasswordStrengthProps) => {
    const activeBarCount = Math.max(0, Math.min(score, BAR_COUNT))

    return (
        // Its own provider: not every app wraps its pages in one
        <TooltipProvider delayDuration={300}>
            <HybridTooltip
                childrenContent={
                    <div className="flex flex-col gap-1">
                        <b>{copy.scores[Math.min(score, copy.scores.length - 1)]}</b>
                        <small>{copy.hint}</small>
                    </div>
                }>
                <div className={twMerge('flex flex-col gap-[3px]', className)}>
                    {Array.from({ length: BAR_COUNT }, (_, barIndex) => {
                        // Bars fill from the bottom up
                        const isActive = barIndex >= BAR_COUNT - activeBarCount

                        return (
                            <div
                                key={barIndex}
                                className={twMerge(
                                    'h-[5px] w-[7px] rounded-[2px] bg-[#dadada]',
                                    isActive && score === 1 && 'bg-[#0077cc]',
                                    isActive && score > 1 && 'bg-[#12c33e]',
                                )}
                            />
                        )
                    })}
                </div>
            </HybridTooltip>
        </TooltipProvider>
    )
}

export default PasswordStrength
